export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export type RepairPdfErrorCode =
  | 'encrypted'
  | 'invalid_pdf'
  | 'unrecoverable'
  | 'browser_runtime'
  | 'repair_error'

export class RepairPdfError extends Error {
  readonly code: RepairPdfErrorCode

  constructor(code: RepairPdfErrorCode, message: string) {
    super(message)
    this.name = 'RepairPdfError'
    this.code = code
  }
}

export interface RepairPdfResult {
  blob: Blob
  originalBytes: number
  repairedBytes: number
}

function classifyQpdfError(message: string): RepairPdfErrorCode {
  if (/password|encrypted|encryption|invalid password|requires.*password/i.test(message)) {
    return 'encrypted'
  }
  if (/not a pdf|invalid pdf|pdf header|unable to find.*trailer|can't find.*trailer/i.test(message)) {
    return 'invalid_pdf'
  }
  if (/damaged|corrupt|xref|object stream|unable to find|recovery failed|parse error/i.test(message)) {
    return 'unrecoverable'
  }
  if (/wasm|worker|sharedarraybuffer|cross-origin|timeout|memory/i.test(message)) {
    return 'browser_runtime'
  }
  return 'repair_error'
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function repairedPdfFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base || 'document'}-repaired.pdf`
}

export async function repairPdf(file: File): Promise<RepairPdfResult> {
  try {
    const { createQpdfRunner } = await import('qpdf-run')
    const runner = await createQpdfRunner({
      workerUrl: new URL('qpdf-run/worker', import.meta.url).href,
      qpdfJsUrl: new URL('qpdf-run/qpdf.js', import.meta.url).href,
      wasmUrl: new URL('qpdf-run/qpdf.wasm', import.meta.url).href,
      timeoutMs: 120_000,
    })

    try {
      const input = new Uint8Array(await file.arrayBuffer())
      const output = await runner.runOne({
        input,
        inputName: 'input.pdf',
        outputName: 'repaired.pdf',
        args: ['--warning-exit-0', 'input.pdf', 'repaired.pdf'],
      })

      if (!output.byteLength) {
        throw new RepairPdfError('repair_error', 'qpdf produced an empty output file')
      }

      return {
        blob: new Blob([output], { type: 'application/pdf' }),
        originalBytes: input.byteLength,
        repairedBytes: output.byteLength,
      }
    } finally {
      await runner.destroy()
    }
  } catch (error) {
    if (error instanceof RepairPdfError) throw error
    const message = error instanceof Error ? error.message : String(error)
    throw new RepairPdfError(classifyQpdfError(message), message)
  }
}
